import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import AnalogyLogo from "@/components/AnalogyLogo";
import { Loader2, CheckCircle2, KeyRound, AlertCircle, MessageSquare } from "lucide-react";
import { toast } from "sonner";

/* Phase 20 onboarding form
 * The same page handles:
 *   1. First-time invite (status: invited → submitted)
 *   2. Re-submission after admin requests changes (status: changes_requested → submitted)
 * Fields are kind-aware (employee / client / vendor).
 */
const FIELD_GROUPS = {
  employee: [
    { k: "phone", l: "Mobile number", required: true },
    { k: "alt_phone", l: "Alternate phone" },
    { k: "title", l: "Title at Analogy", placeholder: "Sr. Designer" },
    { k: "address", l: "Permanent address", textarea: true, full: true },
    { k: "city", l: "City" }, { k: "state", l: "State" },
    { k: "pin", l: "PIN" }, { k: "country", l: "Country", default: "India" },
    { k: "bio", l: "Short bio", textarea: true, full: true, placeholder: "Your background, what you love designing." },
  ],
  client: [
    { k: "phone", l: "Mobile number", required: true },
    { k: "company", l: "Company", required: true },
    { k: "website", l: "Company website" },
    { k: "title", l: "Your role", placeholder: "Founder" },
    { k: "address", l: "Address", textarea: true, full: true },
    { k: "city", l: "City" }, { k: "country", l: "Country", default: "India" },
  ],
  vendor: [
    { k: "company", l: "Company", required: true },
    { k: "phone", l: "Primary phone", required: true },
    { k: "alt_phone", l: "Alt mobile" },
    { k: "website", l: "Website" },
    { k: "address", l: "Address", textarea: true, full: true },
    { k: "city", l: "City" }, { k: "state", l: "State" },
    { k: "pin", l: "PIN" }, { k: "country", l: "Country", default: "India" },
    { k: "category", l: "Category", placeholder: "Tooling / Electronics / Packaging" },
    { k: "specialization", l: "Specialization", placeholder: "CNC, sheet-metal, injection moulding" },
    { k: "gstin", l: "GSTIN" }, { k: "pan", l: "PAN" },
    { k: "payment_terms", l: "Payment terms", placeholder: "Net 30 / 50% advance" },
    { k: "source", l: "How you heard about us", placeholder: "Referral, website…" },
  ],
};

export default function AcceptInvitation() {
  const { token } = useParams();
  const nav = useNavigate();
  const [phase, setPhase] = useState("loading"); // loading | ready | error | done
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!token) { setPhase("error"); setError("Missing invitation token."); return; }
    api.get(`/auth/invitation/${token}`)
      .then(({ data }) => {
        setInvite(data);
        // Pre-fill defaults
        const initial = {};
        (FIELD_GROUPS[data.kind] || FIELD_GROUPS.employee).forEach((f) => {
          if (f.default) initial[f.k] = f.default;
        });
        setForm(initial);
        setPhase("ready");
      })
      .catch((e) => { setError(formatApiError(e)); setPhase("error"); });
  }, [token]);

  const kind = invite?.kind || "employee";
  const groups = FIELD_GROUPS[kind] || FIELD_GROUPS.employee;

  const submit = async (e) => {
    e?.preventDefault?.();
    if (pwd.length < 8) { toast.error("At least 8 characters please."); return; }
    if (pwd !== pwd2) { toast.error("Passwords don't match."); return; }
    const missing = groups.filter((g) => g.required && !((form[g.k] || "").trim()));
    if (missing.length) { toast.error(`Required: ${missing.map((m) => m.l).join(", ")}`); return; }
    setSaving(true);
    try {
      const payload = { token, password: pwd, ...form };
      const { data } = await api.post("/auth/accept-invitation", payload);
      setPhase("done");
      // If legacy/active, log them straight in. New flow requires approval — just show pending state.
      if (data?.status && data.status !== "active") {
        // pending — no login
      } else {
        try {
          await api.post("/auth/login", { email: invite.email, password: pwd });
          setTimeout(() => nav("/"), 1200);
        } catch { setTimeout(() => nav("/login"), 1500); }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-4" data-testid="accept-invitation-page">
      <div className="w-full max-w-2xl card-flat p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <AnalogyLogo size={26} className="text-slate-900" />
          <span className="label-overline text-[#F54800]">Studio OS</span>
        </div>

        {phase === "loading" && (
          <div className="py-10 text-center text-slate-500 text-sm">
            <Loader2 size={24} className="animate-spin mx-auto mb-2 text-[#F54800]" />
            Checking your invitation…
          </div>
        )}

        {phase === "error" && (
          <div className="py-6 text-center" data-testid="invite-error">
            <AlertCircle size={32} className="mx-auto mb-3 text-red-500" />
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Invitation link can&apos;t be used</h1>
            <p className="text-sm text-slate-600 mb-6">{error || "This invitation is invalid, expired, or already used."}</p>
            <Link to="/login" className="text-sm text-[#F54800] hover:underline">Go to login →</Link>
          </div>
        )}

        {phase === "ready" && invite && (
          <>
            <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="onboard-greeting">
              Welcome{invite.name ? `, ${invite.name.split(" ")[0]}` : ""}
            </h1>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              You&apos;ve been invited to join Analogy Studio OS as a <b className="capitalize">{(invite.role || kind).replace(/_/g, " ")}</b>.
              Set a password and fill in your details — an admin will review and approve before you get access. Should take a couple of minutes.
            </p>
            <div className="mt-4 text-[11px] text-slate-500 inline-flex items-center gap-1.5 bg-slate-50 border border-slate-100 rounded-sm px-2 py-1">
              <KeyRound size={11} /> {invite.email}
            </div>

            <form onSubmit={submit} className="space-y-4 mt-6" data-testid="invite-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="label-overline">New password</Label>
                  <Input type="password" className="rounded-sm mt-1" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder="At least 8 characters" autoFocus data-testid="invite-pwd" />
                </div>
                <div>
                  <Label className="label-overline">Confirm password</Label>
                  <Input type="password" className="rounded-sm mt-1" value={pwd2} onChange={(e) => setPwd2(e.target.value)} data-testid="invite-pwd-confirm" />
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100">
                <div className="label-overline mb-3">Your details</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {groups.map((g) => (
                    <div key={g.k} className={g.full ? "sm:col-span-2" : ""}>
                      <Label className="text-[11px] font-semibold text-slate-700">{g.l}{g.required && <span className="text-[#F54800] ml-1">*</span>}</Label>
                      {g.textarea ? (
                        <Textarea rows={3} className="rounded-sm mt-1 resize-none"
                          placeholder={g.placeholder || ""}
                          value={form[g.k] || ""}
                          onChange={(e) => setForm({ ...form, [g.k]: e.target.value })}
                          data-testid={`invite-${g.k}`} />
                      ) : (
                        <Input className="rounded-sm mt-1"
                          placeholder={g.placeholder || ""}
                          value={form[g.k] || ""}
                          onChange={(e) => setForm({ ...form, [g.k]: e.target.value })}
                          data-testid={`invite-${g.k}`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <Button type="submit" disabled={saving} className="w-full rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="invite-submit">
                {saving ? "Submitting…" : "Submit for approval"}
              </Button>
            </form>
          </>
        )}

        {phase === "done" && (
          <div className="py-10 text-center" data-testid="invite-done">
            <CheckCircle2 size={32} className="mx-auto mb-3 text-green-600" />
            <h1 className="font-display text-2xl font-bold text-slate-900">Submitted — thanks!</h1>
            <p className="text-sm text-slate-600 mt-3 max-w-sm mx-auto leading-relaxed">
              Your onboarding is in the admin&apos;s queue. You&apos;ll get an email the moment you&apos;re approved (usually within a working day) — then you can sign in with the password you just set.
            </p>
            <div className="inline-flex items-center gap-1.5 text-[11px] text-slate-500 mt-5 bg-slate-50 border border-slate-100 rounded-sm px-2 py-1">
              <MessageSquare size={11} /> Need to change something? Email <a className="text-[#F54800] hover:underline ml-1" href="mailto:hello@analogydesign.co">hello@analogydesign.co</a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
